/**
 * The keepalive byte budget (vingreact#989 S5b K4).
 *
 * A browser caps the bytes a page may have in flight on the unload-surviving
 * transports: Chromium rejects a `fetch(..., { keepalive: true })` whose body
 * would take the page's keepalive bytes past 64 KiB, and `navigator.sendBeacon`
 * draws on the same quota. The rejection is ASYNCHRONOUS (the fetch promise
 * rejects; nothing throws), so a caller cannot fall back after the fact: it
 * has to know the budget before it sends. This module keeps that count for
 * every keepalive sender in the app (`keepaliveRequest`, the commit beacon and
 * the crash reporter's beacon and keepalive POST).
 *
 * Leaf module on purpose: `client.ts` and `clientErrors.ts` both charge it,
 * and `client.ts` already imports `clientErrors.ts`.
 */
import { convertKeysToSnake } from '@/lib/dataTransform';

/** The per-page keepalive quota (Chromium: 64 KiB of request bodies in flight). */
export const KEEPALIVE_QUOTA_BYTES = 65_536;
/** Kept back from the quota for what this count cannot see. */
export const KEEPALIVE_HEADROOM_BYTES = 1_024;
/**
 * `sendBeacon` reports no settle, so a beacon's bytes are held for this long
 * (the consume state PUT's own timeout): a beacon still counts while the leave
 * pass that sent it runs, and a long session never accumulates them.
 */
export const BEACON_BYTES_HELD_MS = 65_000;

let bytesInFlight = 0;
const beaconTimers = new Set<ReturnType<typeof setTimeout>>();

/** The exact body string `keepaliveRequest` sends for `data`. */
export function keepaliveBody(data: Record<string, unknown>): string {
  return JSON.stringify(convertKeysToSnake(data));
}

/** UTF-8 length of `text`: what the quota counts, not UTF-16 code units. */
export function utf8Bytes(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** Bytes of the body `keepaliveRequest` would send for `data`. */
export function keepaliveBodyBytes(data: Record<string, unknown>): number {
  return utf8Bytes(keepaliveBody(data));
}

/** Keepalive / beacon body bytes this page has in flight right now. */
export function keepaliveBytesInFlight(): number {
  return bytesInFlight;
}

/** What one more keepalive body may take: the quota less headroom less what is in flight. */
export function keepaliveBudgetRemaining(): number {
  return KEEPALIVE_QUOTA_BYTES - KEEPALIVE_HEADROOM_BYTES - bytesInFlight;
}

/**
 * Count `bytes` as in flight until the returned release runs (idempotent).
 * Call it once the transport accepted the request, release it when it settles.
 */
export function chargeKeepaliveBytes(bytes: number): () => void {
  bytesInFlight += bytes;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    bytesInFlight = Math.max(0, bytesInFlight - bytes);
  };
}

/** Count a beacon the browser accepted: held for `BEACON_BYTES_HELD_MS`. */
export function chargeBeaconBytes(bytes: number): void {
  const release = chargeKeepaliveBytes(bytes);
  const timer = setTimeout(() => {
    beaconTimers.delete(timer);
    release();
  }, BEACON_BYTES_HELD_MS);
  beaconTimers.add(timer);
}

/** Test teardown: nothing in flight, no beacon hold pending. */
export function resetKeepaliveBudget(): void {
  for (const timer of beaconTimers) clearTimeout(timer);
  beaconTimers.clear();
  bytesInFlight = 0;
}
